import events from 'lib/events';
import FollowUpApi from 'api/followUp';
import Loader from 'components/loader';
import moment from 'moment';
import phoneFormat from 'util/phoneformat';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import ReportApi from 'api/report';
import ToolTip, {tooltipPositions} from 'components/ToolTip';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import {dateTimeFormats} from 'helpers/constants';
import ReportSummaryErrorHandler from '../components/reportSummaryErrorHandler';
import {makeCancelable} from 'helpers/cancelablePromise';
import SummarySectionPOSDate from '../components/summarySectionPosDate.jsx';
import BulletList from 'helpers/bulletList.jsx';

const dateFormat = 'M/D/YY';


const _ProblemFollowUpsDetailedView = (props) => {
  const {formatMessage, report, emailColumn, clickedDismiss, clickedFollowUp, clickedRevert} = props;

  const   changeRevertClass = (event) => {
    event.target.classList.toggle('bl-revert');
    event.target.classList.toggle('bl-revert-over');
  };


  const hasFollowedUpOrDismissed = (report) => {
    let revert = <i className={'bl fa-2x pointer bl-revert revert-selector'}
                    onMouseOver={changeRevertClass}
                    onMouseLeave={changeRevertClass}
                    onClick={clickedRevert.bind(null, report['ID'])}/>;

    if (report['FollowedUp']) {
      return <span className='follow-up-summary'><i className='bl bl-called'/> {report['FollowedUp']} {revert}</span>;
    }
    if (report['Dismissed']) {
      return <span className='follow-up-summary'><i className='bl bl-cancel'/> {report['Dismissed']} {revert}</span>;
    }
    let f = <i className='bl fa-2x bl-follow-up pointer'
              onClick={clickedFollowUp.bind(null, report['ID'])}/>;
    let d = <i className='bl fa-2x bl-dismiss pointer'
              onClick={clickedDismiss.bind(null, report['ID'])}/>;

    return (
      <div>{f}{d}</div>
    );
  };

  return (
    <table className='table-biolab table-striped follow-up-table'>
      <thead style={{fontSize: '13px'}}>
        <tr>
          <th style={{width: '100px'}}><FormattedMessage id="general.primary.contact"/></th>
          <th><FormattedMessage id="general.primary.phone"/></th>
          <th style={{width: '130px'}}><FormattedMessage id="general.email.address"/></th>
          <th><FormattedMessage id="general.report.last.analysis"/></th>
          <th><FormattedMessage id="general.next.analysis"/></th>
          <th><FormattedMessage id="general.water.tester"/></th>
          <th><FormattedMessage id="general.problem.type"/></th>
          <th style={{width: '120px', paddingTop: '10px', wordBreak: 'break-word'}}>
            <FormattedMessage id="general.date.sent"/><br/><FormattedMessage id="general.by.system"/>
            <ToolTip title={formatMessage({id: 'general.email.status.tooltip'})}
                     position={tooltipPositions.topRight}
                     iconOnly
                     invert/>
          </th>
          <th style={{paddingLeft: '13px', width: '130px', paddingRight: '10px', paddingTop: '10px'}}>
            <FormattedMessage id="general.click.to"/><br/><FormattedMessage id="general.followup"/>
            <ToolTip body={<p><FormattedMessage id="general.followup.tooltip.one"/> <i className='bl bl-follow-up'/>
              <FormattedMessage id="general.followup.tooltip.two"/> <i className='bl bl-dismiss'/>
              <FormattedMessage id="general.followup.tooltip.three"/> <i className='bl bl-revert'/>
              <FormattedMessage id="general.followup.tooltip.four"/></p>}
                     position={tooltipPositions.topRight}
                     iconOnly
                     invert/>
          </th>
        </tr>
      </thead>
      <tbody>
        {
          report.map(row => {
            let customerLink = `#customer/${row.CustomerID}`;

            return (
              <tr key={row.ID}>
                <td style={{wordBreak: 'break-word'}}>
                  <a href={customerLink}>{row.FullName}</a>
                </td>
                <td>{row.HomePhone}</td>
                <td>{row.Email}</td>
                <td style={{width: '67px'}}>{moment(row.PrevAnalysisDate).format(dateFormat)}</td>
                <td style={{width: '67px'}}>{moment(row.NextAnalysisDate).format(dateFormat)}</td>
                <td style={{wordBreak: 'break-word'}}>{row.Tester}</td>
                <td style={{wordBreak: 'break-word'}}>{row.Problems}</td>
                <td style={{wordBreak: 'break-word'}}>
                  {emailColumn(row, formatMessage)}
                </td>
                <td>
                  {hasFollowedUpOrDismissed(row)}
                </td>
              </tr>
            );
          })
        }
      </tbody>
    </table>
  );
};

_ProblemFollowUpsDetailedView.propTypes = {
  formatMessage: PropTypes.func.isRequired,
  report: PropTypes.array.isRequired,
  emailColumn: PropTypes.func.isRequired,
  clickedDismiss: PropTypes.func.isRequired,
  clickedFollowUp: PropTypes.func.isRequired,
  clickedRevert: PropTypes.func.isRequired,
};


class ProblemFollowUps extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: true,
      summary: null,
      report: null,
      start: moment().subtract(2, 'weeks'),
      end: moment(),
      startLabel: moment().subtract(2, 'weeks').format(dateTimeFormats.numeralDate),
      endLabel: moment().format(dateTimeFormats.numeralDate),
      showNoDataMessage: false,
      error: false,
    };
    this.clickedDismiss = this.clickedDismiss.bind(this);
    this.clickedFollowUp = this.clickedFollowUp.bind(this);
    this.clickedRevert = this.clickedRevert.bind(this);
  }

  componentDidMount() {
    const {start, end} = this.state;

    this.setState({
      loaded: false,
      error: false,
    });

    if (this.fetching && !this.fetching.isFinished) {
      this.fetching.cancel();
    }else{
      this.setState({
        summary: null,
      });
    }

    const conditionalPush = (condition, list, pushValue) => {
      if (condition) {
        list.push(pushValue);
      }
    };

    (this.fetching = makeCancelable(ReportApi.getFollowUpReport(start.format(), end.format())))
      .then(data => {
        if (data.report) {
          let {formatMessage} = this.props.intl;

          for (let row of data.report) {
            row.FullName = row.NameFirst + ' ' + row.NameLast;
            let problems = [];
            //Created string list of all problems of follow-up

            conditionalPush(row.Cloudy, problems, formatMessage({id: 'general.cloudy'}));
            conditionalPush(row.Hazy, problems, formatMessage({id: 'general.hazy'}));
            conditionalPush(row.Foaming, problems, formatMessage({id: 'preferences.foaming'}));
            conditionalPush(row.BlackAlgae, problems, formatMessage({id: 'general.black.algae'}));
            conditionalPush(row.GreenAlgae, problems, formatMessage({id: 'general.green.algae'}));
            conditionalPush(row.MustardAlgae, problems, formatMessage({id: 'general.mustard.algae'}));
            conditionalPush(row.SpaAlgae, problems, formatMessage({id: 'general.spa.algae'}));
            conditionalPush(row.SlimeMold, problems, formatMessage({id: 'preferences.slime.mold'}));
            conditionalPush(row.Swampy, problems, formatMessage({id: 'preferences.swampy'}));
            conditionalPush(row.CopperStaining, problems, formatMessage({id: 'scan.copper.staining'}));
            conditionalPush(row.IronStaining, problems, formatMessage({id: 'scan.iron.staining'}));
            conditionalPush(row.UnknownStaining, problems, formatMessage({id: 'scan.unknown.staining'}));
            conditionalPush(row.ScaleBuildup, problems, formatMessage({id: 'scan.scale.buildup'}));
            conditionalPush(row.WaterlineBuildup, problems, formatMessage({id: 'scan.waterline.buildup'}));
            conditionalPush(row.OrganicStaining, problems, formatMessage({id: 'scan.organic.staining'}));
            conditionalPush(row.ChlorineDemand, problems, formatMessage({id: 'general.chlorine.demand'}));
            row.Problems = problems.join(', ');
            row.HomePhone = row.HomePhone ? phoneFormat(row.HomePhone) : '';
            row.FollowedUp = row.FollowedUp ? moment.utc(row.FollowedUp).format(dateTimeFormats.numeralDate) : '';
            row.Dismissed = row.Dismissed ? moment.utc(row.Dismissed).format(dateTimeFormats.numeralDate) : '';
            row.Created = row.Created ? moment.utc(row.Created).format(dateTimeFormats.numeralDate) : '';

            let FollowUpDate = row['FollowUpDate'] ? moment.utc(row['FollowUpDate']) : '';

            if (!row.Email || row.Email.trim() === '') {
              row.EmailString = formatMessage({id: 'general.no.email'});
            }
            if (!FollowUpDate || FollowUpDate.isAfter(moment.utc())) {
              row.EmailString = formatMessage({id: 'general.email.not.sent'});
            }
            if (!row['Emailed'] && FollowUpDate.isBefore(moment.utc())) {
              row.EmailString = formatMessage({id: 'general.email.opted.out'});
            }
            if (row.FollowUpDate) {
              row.EmailString = moment(row.FollowUpDate).format(dateTimeFormats.numeralDate);
            }
            if (row.NextAnalysisDate) {
              row.NextAnalysisDate = moment(row.NextAnalysisDate).format(dateTimeFormats.numeralDate);
            }
            if (row.Created) {
              row.Created = moment(row.Created).format(dateFormat);
            }
          }
        }

        if (data && data.summary.total > 0) {
          this.setState({
            summary: data.summary,
            report: data.report,
            startLabel: start.format(dateTimeFormats.numeralDate),
            endLabel: end.format(dateTimeFormats.numeralDate),
            showNoDataMessage: false,
          });
        } else {
          this.setState({showNoDataMessage: true});
        }
      }).catch(err => {
        console.log(err);
        if (err && err.canceled) {return;}
        this.setState({showNoDataMessage: true});
      }).finally(() => {
        this.setState({
          loaded: true,
        });
      });
  };
  componentWillUnmount() {
    if (this.fetching && !this.fetching.isFinished) {
      this.fetching.cancel();
    }
    if (this.followUpAction && !this.followUpAction.isFinished) {
      this.followUpAction.cancel();
    }
  }

  clickedDismiss(id) {
    if (this.followUpAction && !this.followUpAction.isFinished) {
      this.followUpAction.cancel();
    }
    (this.followUpAction = makeCancelable(FollowUpApi.dismiss(id)))
    .then(() => {
      this.state.report.find(x => x.ID === id)['Dismissed'] = moment.utc().format(dateTimeFormats.numeralDate);
      this.setState({report: this.state.report});
    })
    .catch(error => events.publish('error.user', error));
  }

  clickedFollowUp(id) {
    if (this.followUpAction && !this.followUpAction.isFinished) {
      this.followUpAction.cancel();
    }
    (this.followUpAction = makeCancelable(FollowUpApi.followUp(id)))
    .then(() => {
      this.state.report.find(x => x.ID === id)['FollowedUp'] = moment.utc().format('M/DD/YYYY');
      this.setState({report: this.state.report});
    })
    .catch(error => events.publish('error.user', error));
  }

  clickedRevert(id) {
    if (this.followUpAction && !this.followUpAction.isFinished) {
      this.followUpAction.cancel();
    }
    (this.followUpAction = makeCancelable(FollowUpApi.revert(id)))
    .then(() => {
      let report = this.state.report.find(x => x.ID === id);

      report['FollowedUp'] = '';
      report['Dismissed'] = '';
      this.setState({report: this.state.report});
    })
    .catch(error => events.publish('error.user', error));
  }

  emailColumn(report, formatMessage) {
    let FollowUpDate = report['FollowUpDate'] ? moment.utc(report['FollowUpDate']) : '';

    if (!report.Email || report.Email.trim() === '') {
      return <div style={{display: 'flex', alignItems: 'baseline'}}><div><i style={{verticalAlign: 'sub'}} className='bl bl-attention red'/></div><span style={{marginLeft: '5px'}}>{formatMessage({id: 'general.no.email'})}</span></div>;
    }
    if (!FollowUpDate || FollowUpDate.isAfter(moment.utc())) {
      return <div style={{display: 'flex', alignItems: 'baseline'}}><div><i style={{verticalAlign: 'sub'}} className='bl bl-attention red'/></div><span style={{marginLeft: '5px'}}>{formatMessage({id: 'general.email.not.sent'})}</span></div>;
    }
    if (!report['Emailed'] && FollowUpDate.isBefore(moment.utc())) {
      return <div style={{display: 'flex', alignItems: 'baseline'}}><div><i style={{verticalAlign: 'sub'}} className='bl bl-opted-out'/></div><span style={{marginLeft: '5px'}}>{formatMessage({id: 'general.email.opted.out'})}</span></div>;
    }
    return <div>{FollowUpDate.format(dateFormat)}</div>;
  }

  exportReport = () => {
    const {start, end} = this.state;

    ReportApi.getFollowUpReportExport(
      this.props.intl.locale,
      start.format(), end.format(), false);
  };

  render() {
    let {formatMessage} = this.props.intl;
    const {summary, loaded, report, startLabel, endLabel, showNoDataMessage} = this.state;

    return (
      <div className='report-container'>
        <div className='description'>
          <FormattedMessage id="reports.problem.followups.description" />
        </div>
        <div>
          <FormattedMessage id="reports.thingstoknow" />
          <FormattedMessage id="reports.thingstoknow.follow-ups">
            {x => <BulletList text={x} />}
          </FormattedMessage>
        </div>
        <Loader loaded={loaded}>
          <ReportSummaryErrorHandler error={this.state.error}>
            {summary ?
              <div className='col-sm-12 summary-container'>
                <div className='row'>
                  <h2>
                    <FormattedMessage id="reports.report.summary"/>
                    <span className='summary-date'> | <FormattedMessage id="general.date.range.labelled"
                                                                      values={{
                                                                        start: startLabel,
                                                                        end: endLabel,
                                                                      }}/>
                    </span>
                  </h2>
                  <SummarySectionPOSDate POSActive={this.props.POSActive} dealer={this.props.dealer} />
                </div>
                <div className='pad-top'>
                  {report ?
                    <_ProblemFollowUpsDetailedView formatMessage={formatMessage}
                    report={report} emailColumn={this.emailColumn}
                    clickedDismiss={this.clickedDismiss}
                    clickedFollowUp={this.clickedFollowUp}
                    clickedRevert={this.clickedRevert}/> : <div/>}
                </div>
              </div> :
              <div className='summary-container'>
                <h2><FormattedMessage id="reports.report.summary"/></h2>
                <p><FormattedMessage id="reports.reveal.report.summary"/></p>
                <SummarySectionPOSDate POSActive={this.props.POSActive} dealer={this.props.dealer} />
                {showNoDataMessage ?
                  <label className="report-no-data"><FormattedMessage id="reports.error.no.results"/></label> : ''}
                <div className="container-fluid custom-container-margins">
                  <div/>
                </div>
              </div>}
            {!showNoDataMessage ?
              <div className='col-sm-12 button-group shift-right'>
                <button className='btn btn-primary pull-right'
                    onClick={this.exportReport}><FormattedMessage id="reports.export.report"/></button>
              </div> : null}
          </ReportSummaryErrorHandler>
        </Loader>
      </div>
    );
  }
}

ProblemFollowUps.propTypes = {
  POSActive: PropTypes.bool,
  intl: intlShape.isRequired,
  dealer: PropTypes.any,
};

ProblemFollowUps.defaultProps = {
  POSActive: false,
};

export default injectIntl(ProblemFollowUps);

